import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import {
  Ticket, Zap, ShieldCheck, TrendingUp, Users, Mail, QrCode, ArrowRight,
  CheckCircle2, DollarSign, Sparkles, Compass, Tags, Gift, Layers,
  BarChart3, Share2, Building2, Globe, Wallet, Clock, ChevronLeft,
  ChevronRight, Flame, Repeat,
} from 'lucide-react';
import SEO from '../components/SEO';
import { breadcrumbLD, faqLD } from '../utils/structuredData';

const SITE_URL = (typeof window !== 'undefined' && window.location?.origin) || 'https://eventism.com';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Matches backend DEFAULT_FEE_CONFIG — replaced by /api/pricing/ticket-fees at runtime.
const DEFAULT_FEES = { percent: 3.7, flat_cents: 179, payment_processing_percent: 2.9 };
const svcLabel = (f) => `${f.percent}% + $${(f.flat_cents / 100).toFixed(2)}`;

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------
const TRUST_METRICS = [
  { label: 'Stripe payouts', value: '24 hours' },
  { label: 'Free marketing credit', value: '$25' },
];

const HERO_SLIDES = [
  {
    id: 'creators',
    image: 'https://images.unsplash.com/photo-1459749411175-04bf5292ceea?w=1600&q=75&auto=format&fit=crop',
    badge: 'SOLD OUT · 1,200 TICKETS',
    badgeIcon: Flame,
    kicker: 'The wait is over',
    line1: 'Finally,',
    line2: 'A Platform for Event Creators!',
    body: 'Your events, your brand, your audience. Picture the night: doors packed, scanner buzzing, zero stress.',
    cta: 'Start Selling Tickets',
    ctaTo: '/organizations/apply',
  },
  {
    id: 'sold-out',
    image: 'https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1600&q=75&auto=format&fit=crop',
    badge: 'SOLD OUT · 3 WEEKS EARLY',
    badgeIcon: TrendingUp,
    kicker: 'The transformation',
    line1: 'This is what your',
    line2: 'next event looks like.',
    body: 'Built-in email blasts, promo codes and a CRM that remembers every past buyer — so tickets move before the week of.',
    cta: 'Start Selling Tickets',
    ctaTo: '/organizations/apply',
  },
  {
    id: 'series-clubs',
    image: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1600&q=75&auto=format&fit=crop',
    badge: 'RESIDUAL INCOME',
    badgeIcon: Repeat,
    kicker: 'Series & Clubs',
    line1: 'Turn one great night',
    line2: 'into monthly income.',
    body: 'Launch a Series or Club with memberships and recurring perks — your biggest fans pay monthly, not just per ticket.',
    cta: 'Build Your Series',
    ctaTo: '/organizations/apply',
  },
  {
    id: 'vibes',
    image: 'https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1600&q=75&auto=format&fit=crop',
    badge: 'SOLD OUT · ENCORE ADDED',
    badgeIcon: Flame,
    kicker: 'Great vibes are great business',
    line1: 'Give them a night',
    line2: 'they brag about.',
    body: 'Add-on upsells, VIP tiers and lightning-fast QR doors — the experience feels premium because it is.',
    cta: 'Start Selling Tickets',
    ctaTo: '/organizations/apply',
  },
];

const SYNDICATION_TARGETS = [
  'Eventbrite', 'Google Event Search', 'Facebook Events', 'Patch.com',
  'PR Newswire', 'Local press lists', 'Embeddable widgets',
];

const FEATURE_TABS = [
  {
    id: 'tiered-tickets',
    label: 'Tiered Tickets',
    icon: Layers,
    title: 'Every tier, every price, one page',
    body: 'Early Bird, GA, VIP, group bundles, timed releases and waitlists — build a full ticket ladder in minutes with per-tier caps and refund rules.',
    preview: [
      { name: 'Early Bird', price: '$25', meta: 'Sold out', tone: 'text-muted-foreground line-through' },
      { name: 'General Admission', price: '$35', meta: '214 left', tone: 'text-foreground' },
      { name: 'VIP + Lounge', price: '$95', meta: '18 left', tone: 'text-indigo-400' },
    ],
  },
  {
    id: 'add-ons',
    label: 'Add-ons & Upsells',
    icon: Gift,
    title: 'Sell more than the ticket',
    body: 'T-shirts, open-bar wristbands, parking, VIP upgrades — attach add-ons with sizes and variations that appear right in checkout, after the ticket is picked.',
    preview: [
      { name: 'Event T-Shirt (S–XXL)', price: '+$20', meta: 'Add-on', tone: 'text-foreground' },
      { name: 'Open Bar Wristband', price: '+$45', meta: 'Add-on', tone: 'text-foreground' },
      { name: 'VIP Parking', price: '+$15', meta: 'Add-on', tone: 'text-foreground' },
    ],
  },
  {
    id: 'promo-codes',
    label: 'Promo Codes',
    icon: Tags,
    title: 'Discounts that track themselves',
    body: 'Percentage or fixed-amount codes, per-tier targeting, usage caps and expiry dates. See exactly which code sold which ticket.',
    preview: [
      { name: 'EARLYBIRD20', price: '−20%', meta: '142 uses', tone: 'text-emerald-400' },
      { name: 'INFLUENCER-JAY', price: '−$10', meta: '67 uses', tone: 'text-emerald-400' },
      { name: 'VIPUPGRADE', price: '−15%', meta: 'VIP only', tone: 'text-emerald-400' },
    ],
  },
  {
    id: 'qr-checkin',
    label: 'QR Check-in',
    icon: QrCode,
    title: 'Doors that move fast',
    body: 'Scan with any smartphone camera — no hardware to buy. Instant validation, duplicate detection, and live check-in counts for your door team.',
    preview: [
      { name: 'Scanned · A. Rivera', price: '✓', meta: 'GA · 8:02 PM', tone: 'text-emerald-400' },
      { name: 'Scanned · M. Chen', price: '✓', meta: 'VIP · 8:03 PM', tone: 'text-emerald-400' },
      { name: 'Duplicate blocked', price: '✕', meta: '8:04 PM', tone: 'text-red-400' },
    ],
  },
];

const ORGANIZER_TOOLS = [
  {
    icon: Users,
    title: 'Ticket-holder CRM',
    body: 'Every buyer, every purchase, in one place. Segment by event, spend, or behavior and build audiences you actually own — export anytime.',
  },
  {
    icon: Mail,
    title: 'Email & SMS marketing',
    body: 'Branded campaigns from your dashboard at $0.001 per email and $0.015 per SMS. Preview, test-send, and get open & click reports per blast.',
  },
  {
    icon: BarChart3,
    title: 'Real-time analytics',
    body: 'Live sales velocity, page views vs unique visitors, revenue by tier, and check-in rates — know what is working while tickets are still on sale.',
  },
  {
    icon: Share2,
    title: 'One-click syndication',
    body: 'Push your event to Eventbrite, press lists, and local outlets straight from the dashboard. One listing, everywhere your buyers look.',
  },
];

const buildFaqs = (fees) => {
  const svc = svcLabel(fees);
  const pp = fees.payment_processing_percent;
  return [
    {
      q: 'When do I receive ticket payouts?',
      a: 'Stripe Express handles payouts — funds settle 24 hours after your event ends. High-volume organizers unlock rolling daily payouts after their third sold-out event.',
    },
    {
      q: 'Can I bring my own door scanners?',
      a: 'You don\'t need special hardware. Any smartphone camera works with our check-in scanner, and standard Bluetooth scanners are supported too.',
    },
    {
      q: 'Can I import my existing contact lists?',
      a: 'Yes — upload a CSV and your contacts land in your CRM ready to segment and email. We never hold your list hostage; export it any time.',
    },
    {
      q: 'Is there a contract or monthly fee?',
      a: `No contracts, no monthly software fee, no setup cost. Listing is free — buyers pay a ${svc} service fee per sold ticket plus ${pp}% payment processing (which covers Stripe, our processor).`,
    },
    {
      q: 'What does marketing cost?',
      a: 'Email campaigns are $0.001 per send and SMS blasts $0.015 per text, billed against a prepaid wallet. Every new organization starts with $25 in free credit.',
    },
  ];
};

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------
const SectionLabel = ({ children }) => (
  <p className="text-sm font-semibold uppercase tracking-[0.15em] text-indigo-500 dark:text-indigo-300">{children}</p>
);

function LifestyleHeroSlider() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 5500);
    return () => clearInterval(t);
  }, [paused]);
  const go = (i) => setIdx((i + HERO_SLIDES.length) % HERO_SLIDES.length);

  return (
    <section
      className="relative h-[540px] sm:h-[600px] overflow-hidden bg-slate-950"
      data-testid="lifestyle-hero-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {HERO_SLIDES.map((s, i) => {
        const BadgeIcon = s.badgeIcon;
        const on = i === idx;
        return (
          <div
            key={s.id}
            className={`absolute inset-0 transition-opacity duration-700 ${on ? 'opacity-100 z-10' : 'opacity-0 z-0 pointer-events-none'}`}
            data-testid={`hero-slide-${s.id}`}
            aria-hidden={!on}
          >
            <img src={s.image} alt={s.kicker} className="absolute inset-0 w-full h-full object-cover" loading={i === 0 ? 'eager' : 'lazy'} />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/70 to-slate-950/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/40" />
            <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col justify-center">
              <div className={`max-w-2xl space-y-5 ${on ? 'animate-fade-in-up' : ''}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-red-500/90 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-white shadow-lg shadow-red-500/30 rotate-[-2deg]">
                    <BadgeIcon className="h-3.5 w-3.5" /> {s.badge}
                  </span>
                  <span className="text-xs uppercase tracking-[0.2em] text-slate-300 font-semibold">{s.kicker}</span>
                </div>
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.05] text-white">
                  {s.line1}{' '}
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-fuchsia-400 to-indigo-400">
                    {s.line2}
                  </span>
                </h2>
                <p className="text-base sm:text-lg text-slate-200 leading-relaxed max-w-xl">{s.body}</p>
                <Button asChild size="lg" className="rounded-full btn-glow bg-white hover:bg-slate-100 text-slate-950 hover:text-slate-950 text-base px-8 font-semibold">
                  <Link to={s.ctaTo} data-testid={`hero-slide-cta-${s.id}`}>
                    {s.cta} <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        );
      })}
      {/* Controls */}
      <button
        onClick={() => go(idx - 1)}
        className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white/25 transition-colors"
        aria-label="Previous slide" data-testid="hero-slider-prev"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        onClick={() => go(idx + 1)}
        className="absolute right-3 sm:right-5 top-1/2 -translate-y-1/2 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 backdrop-blur-md text-white hover:bg-white/25 transition-colors"
        aria-label="Next slide" data-testid="hero-slider-next"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.id}
            onClick={() => go(i)}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`hero-slider-dot-${i}`}
            className={`h-2 rounded-full transition-all duration-300 ${i === idx ? 'w-8 bg-white' : 'w-2 bg-white/40 hover:bg-white/70'}`}
          />
        ))}
      </div>
    </section>
  );
}

function HeroMock() {
  return (
    <Card className="border-border bg-card/80 backdrop-blur-xl shadow-2xl shadow-indigo-500/10 animate-fade-in-up" data-testid="hero-ticket-mock">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-heading font-semibold text-lg">Warehouse Sessions Vol. 4</p>
            <p className="text-xs text-muted-foreground">Sat, Oct 24 · Your venue, your brand</p>
          </div>
          <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30">On sale</Badge>
        </div>
        <div className="space-y-2">
          {[
            { t: 'Early Bird', p: '$25', s: 'Sold out', dim: true },
            { t: 'General Admission', p: '$35', s: '214 left' },
            { t: 'VIP + Open Bar add-on', p: '$95', s: '18 left', vip: true },
          ].map((r) => (
            <div key={r.t} className={`flex items-center justify-between rounded-lg border px-4 py-3 ${r.vip ? 'border-indigo-500/40 bg-indigo-500/5' : 'border-border'} ${r.dim ? 'opacity-50' : ''}`}>
              <div className="flex items-center gap-2">
                <Ticket className={`h-4 w-4 ${r.vip ? 'text-indigo-400' : 'text-muted-foreground'}`} />
                <span className="text-sm font-medium">{r.t}</span>
              </div>
              <div className="text-right">
                <span className="text-sm font-heading font-bold">{r.p}</span>
                <p className="text-[10px] text-muted-foreground">{r.s}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between rounded-lg bg-muted/60 px-4 py-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <TrendingUp className="h-4 w-4 text-emerald-500" /> Sales velocity: 31 tickets / hr
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <QrCode className="h-4 w-4 text-indigo-400" /> 412 checked in
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FeatureShowcase() {
  const [active, setActive] = useState(FEATURE_TABS[0].id);
  const tab = FEATURE_TABS.find((t) => t.id === active);
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="ticketing-features-section">
      <div className="max-w-2xl space-y-3 mb-10">
        <SectionLabel>The ticket engine</SectionLabel>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold tracking-tight">
          Ticketing that sells harder than a box office
        </h2>
      </div>
      <div className="grid lg:grid-cols-12 gap-6 lg:gap-12 items-start">
        <div className="lg:col-span-5 space-y-2">
          {FEATURE_TABS.map((t) => {
            const Icon = t.icon;
            const on = t.id === active;
            return (
              <button
                key={t.id}
                onClick={() => setActive(t.id)}
                data-testid={`feature-tab-${t.id}`}
                className={`w-full text-left rounded-xl border px-5 py-4 transition-colors duration-200 ${on ? 'border-indigo-500/50 bg-indigo-500/5' : 'border-border hover:bg-accent/50'}`}
              >
                <div className="flex items-center gap-3">
                  <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${on ? 'bg-indigo-500 text-white' : 'bg-muted text-muted-foreground'}`}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="font-heading font-semibold">{t.label}</span>
                </div>
                {on && <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{t.body}</p>}
              </button>
            );
          })}
        </div>
        <div className="lg:col-span-7">
          <Card className="border-border bg-card/80 backdrop-blur-xl card-lift" data-testid="feature-preview-card">
            <CardContent className="p-6 sm:p-8 space-y-5">
              <h3 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight">{tab.title}</h3>
              <div className="space-y-2">
                {tab.preview.map((row) => (
                  <div key={row.name} className="flex items-center justify-between rounded-lg border border-border px-4 py-3.5">
                    <span className={`text-sm font-medium ${row.tone}`}>{row.name}</span>
                    <div className="text-right">
                      <span className="text-sm font-heading font-bold">{row.price}</span>
                      <p className="text-[10px] text-muted-foreground">{row.meta}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-indigo-400" /> Mock preview — build yours in minutes
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}

function FeeCalculator({ fees }) {
  const [tickets, setTickets] = useState([500]);
  const [price] = useState(35);
  const n = tickets[0];
  const math = useMemo(() => {
    const subtotal = n * price;
    const service = subtotal * (fees.percent / 100) + n * (fees.flat_cents / 100);
    const processing = subtotal * (fees.payment_processing_percent / 100);
    return { subtotal, service, processing, buyersPay: service + processing };
  }, [n, price, fees]);
  const fmt = (v) => v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="pricing-section">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className="space-y-5">
          <SectionLabel>Transparent pricing</SectionLabel>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold tracking-tight" data-testid="pricing-headline">
            {svcLabel(fees)} per ticket. <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-500 via-indigo-500 to-amber-500">That&apos;s it.</span>
          </h2>
          <p className="text-base text-muted-foreground leading-relaxed">
            Plus {fees.payment_processing_percent}% payment processing that covers Stripe, our processor — all paid by the buyer at checkout by default. No monthly software fee, no contracts, and you keep 100% of your ticket price.
          </p>
          <ul className="space-y-2.5">
            {['Free event listings, unlimited tiers & add-ons', 'You receive the full ticket price — buyers cover the fees', 'Stripe payouts 24 hours after your event', '$25 marketing wallet credit for every new organizer'].map((li) => (
              <li key={li} className="flex items-start gap-2.5 text-sm">
                <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {li}
              </li>
            ))}
          </ul>
          <Button asChild size="lg" className="rounded-full btn-glow bg-indigo-600 hover:bg-indigo-500 text-white hover:text-white">
            <Link to="/organizations/apply" data-testid="pricing-start-selling-button">
              Start selling with zero upfront cost <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
        <Card className="border-indigo-500/30 bg-card/80 backdrop-blur-xl shadow-xl shadow-indigo-500/10">
          <CardContent className="p-6 sm:p-8 space-y-6">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-indigo-400" />
              <h3 className="text-xl font-heading font-semibold">See the math on your event</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Tickets sold (${price} avg)</span>
                <span className="font-heading font-bold text-lg" data-testid="fee-ticket-count">{n.toLocaleString()}</span>
              </div>
              <Slider value={tickets} onValueChange={setTickets} min={50} max={5000} step={50} data-testid="fee-calculator-slider" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-border p-4">
                <p className="text-xs text-muted-foreground">Service fee ({svcLabel(fees)})</p>
                <p className="text-xl font-heading font-bold mt-1" data-testid="fee-service-display">{fmt(math.service)}</p>
                <p className="text-[10px] text-muted-foreground mt-1">Paid by buyers</p>
              </div>
              <div className="rounded-xl border border-border p-4">
                <p className="text-xs text-muted-foreground">Processing ({fees.payment_processing_percent}%)</p>
                <p className="text-xl font-heading font-bold mt-1" data-testid="fee-processing-display">{fmt(math.processing)}</p>
                <p className="text-[10px] text-muted-foreground mt-1">Covers Stripe — paid by buyers</p>
              </div>
            </div>
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-4 text-center">
              <p className="text-xs text-emerald-500 font-semibold uppercase tracking-wider">You receive — 100% of ticket sales</p>
              <p className="text-3xl font-display font-bold text-emerald-500 mt-1" data-testid="fee-savings-display">{fmt(math.subtotal)}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function TicketingHomePage() {
  const [fees, setFees] = useState(DEFAULT_FEES);
  useEffect(() => {
    axios.get(`${API}/pricing/ticket-fees`)
      .then((res) => setFees({
        percent: res.data.percent,
        flat_cents: res.data.flat_cents,
        payment_processing_percent: res.data.payment_processing_percent,
      }))
      .catch(() => {});
  }, []);
  const faqs = useMemo(() => buildFaqs(fees), [fees]);
  const breadcrumb = breadcrumbLD([{ name: 'Home', url: '/' }]);
  const faqGraph = faqLD(faqs.map((f) => ({ question: f.q, answer: f.a })));
  const metrics = [{ label: 'Service fee per ticket', value: svcLabel(fees) }, ...TRUST_METRICS];
  return (
    <div className="min-h-screen bg-background pt-16" data-testid="ticketing-home-page">
      <SEO
        title="Eventism — Sell Tickets Online with Built-in Marketing & CRM"
        description="The all-in-one ticketing platform for event organizers: tiered tickets, add-on upsells, promo codes, QR check-in, email marketing, CRM, Series & Clubs recurring revenue, and real-time analytics. Transparent per-ticket fees, 24-hour payouts, $25 free marketing credit."
        canonical={`${SITE_URL}/`}
        url={`${SITE_URL}/`}
        keywords={[
          'event ticketing platform', 'sell tickets online', 'eventbrite alternative',
          'ticketing with crm', 'event marketing tools', 'qr check-in', 'ticket add-ons',
        ]}
        structuredData={[breadcrumb, faqGraph].filter(Boolean)}
      />

      {/* Attendee redirect banner */}
      <div className="border-b border-border/60 bg-muted/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-center gap-2 text-xs sm:text-sm">
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-500" />
          </span>
          <span className="text-muted-foreground">Looking for concerts, nightlife &amp; local experiences?</span>
          <Link to="/discover" className="font-semibold text-indigo-500 dark:text-indigo-300 hover:underline inline-flex items-center gap-1" data-testid="hero-discover-link">
            Explore events <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      {/* Lifestyle hero slider — the transformation, sold-out vibes */}
      <LifestyleHeroSlider />

      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="home-hero-section">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-indigo-500/15 blur-3xl" />
          <div className="absolute top-40 -left-24 h-72 w-72 rounded-full bg-fuchsia-500/10 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 sm:pt-20 pb-16 sm:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <div className="lg:col-span-7 space-y-6">
            <Badge variant="outline" className="border-indigo-500/30 bg-indigo-500/10 text-indigo-500 dark:text-indigo-300 tracking-[0.12em] uppercase text-[11px]">
              Next-gen ticket engine &amp; CRM for organizers
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight leading-[1.1]">
              Sell out every event.{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-500 via-indigo-500 to-amber-500">
                Keep control of your brand.
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-xl">
              Modular ticket tiers, instant add-on upsells, promo codes, built-in audience CRM and marketing — one platform, one flat fee, built for independent promoters and venue operators.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="rounded-full btn-glow bg-indigo-600 hover:bg-indigo-500 text-white hover:text-white text-base px-8">
                <Link to="/organizations/apply" data-testid="hero-start-selling-button">
                  Start Selling Tickets <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-full text-base px-8">
                <a href="#pricing" data-testid="hero-calculator-button">Calculate your savings</a>
              </Button>
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-3 pt-2">
              {metrics.map((m) => (
                <div key={m.label}>
                  <p className="font-heading font-bold text-lg">{m.value}</p>
                  <p className="text-xs text-muted-foreground">{m.label}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-5">
            <HeroMock />
          </div>
        </div>
      </section>

      {/* Syndication marquee */}
      <section className="border-y border-border/60 bg-muted/30 py-6 overflow-hidden" data-testid="syndication-marquee">
        <p className="text-center text-xs uppercase tracking-[0.2em] text-muted-foreground mb-4">One-click sync to major discovery networks</p>
        <div className="flex flex-wrap items-center justify-center gap-3 px-4">
          {SYNDICATION_TARGETS.map((t) => (
            <span key={t} className="rounded-full border border-border bg-card px-4 py-1.5 text-sm font-medium text-muted-foreground">
              {t}
            </span>
          ))}
        </div>
      </section>

      <FeatureShowcase />

      {/* Organizer tools */}
      <section className="bg-muted/30 border-y border-border/60" data-testid="organizer-tools-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="max-w-2xl space-y-3 mb-10">
            <SectionLabel>Growth infrastructure</SectionLabel>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold tracking-tight">
              The tools legacy platforms charge extra for
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {ORGANIZER_TOOLS.map((t) => {
              const Icon = t.icon;
              return (
                <Card key={t.title} className="border-border bg-card/80 card-lift" data-testid={`tool-card-${t.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <CardContent className="p-6 space-y-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-500/10 text-indigo-500 dark:text-indigo-300">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="font-heading font-semibold text-lg">{t.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t.body}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Profiles */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24" data-testid="profiles-section">
        <div className="max-w-2xl space-y-3 mb-10">
          <SectionLabel>Your brand, front and center</SectionLabel>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold tracking-tight">
            Organizer &amp; venue pages that build a following
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-border bg-card/80 card-lift">
            <CardContent className="p-6 sm:p-8 space-y-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-fuchsia-500/10 text-fuchsia-500">
                <Users className="h-5 w-5" />
              </span>
              <h3 className="font-heading font-semibold text-xl">Organizer profiles</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                A public home for your brand — logo, photo gallery, past-event highlights, memberships, and direct messaging. Fans hit Follow and get notified the moment you publish your next event.
              </p>
              <ul className="space-y-2">
                {['Follower notifications on every new event', 'Direct messages routed to your inbox', 'Series & membership showcases'].map((li) => (
                  <li key={li} className="flex items-start gap-2.5 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {li}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          <Card className="border-border bg-card/80 card-lift">
            <CardContent className="p-6 sm:p-8 space-y-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/10 text-amber-500">
                <Building2 className="h-5 w-5" />
              </span>
              <h3 className="font-heading font-semibold text-xl">Venue pages &amp; widgets</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                SEO-ready venue profiles with your full calendar, plus an embeddable checkout widget so buyers can purchase without ever leaving your own website.
              </p>
              <ul className="space-y-2">
                {['Embeddable ticket widget for your site', 'Search-optimized venue & city pages', 'Reservations and experiences alongside events'].map((li) => (
                  <li key={li} className="flex items-start gap-2.5 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {li}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
        {/* Series & Clubs — residual income banner */}
        <Card className="mt-6 border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 via-card to-indigo-500/10 card-lift" data-testid="series-clubs-banner">
          <CardContent className="p-6 sm:p-8 grid md:grid-cols-[auto_1fr_auto] items-center gap-6">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-500 shrink-0">
              <Repeat className="h-7 w-7" />
            </span>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading font-semibold text-xl">Series &amp; Clubs — your residual income engine</h3>
                <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30">Recurring revenue</Badge>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-2xl">
                Group your events into a Series or launch a Club with paid memberships. Fans subscribe for perks, discounts and first access — you earn every month, not just on show night, and every new drop notifies your members automatically.
              </p>
            </div>
            <Button asChild className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-white hover:text-white shrink-0">
              <Link to="/organizations/apply" data-testid="series-clubs-cta">
                Build Your Series <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </section>

      <div id="pricing"><FeeCalculator fees={fees} /></div>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24" data-testid="faq-section">
        <div className="space-y-3 mb-8">
          <SectionLabel>Questions, answered</SectionLabel>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">Before you flip the switch</h2>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} data-testid="faq-accordion-item">
              <AccordionTrigger className="text-left font-heading font-semibold">{f.q}</AccordionTrigger>
              <AccordionContent className="text-sm text-muted-foreground leading-relaxed">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="final-cta-section">
        <div className="relative overflow-hidden rounded-3xl border border-indigo-500/30 bg-slate-950 px-6 sm:px-12 py-14 sm:py-20 text-center">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-24 left-1/4 h-72 w-72 rounded-full bg-indigo-500/20 blur-3xl" />
            <div className="absolute -bottom-24 right-1/4 h-72 w-72 rounded-full bg-fuchsia-500/15 blur-3xl" />
          </div>
          <div className="relative space-y-5">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight text-white">
              Ready to run your best event yet?
            </h2>
            <p className="text-base sm:text-lg text-slate-300 max-w-xl mx-auto">
              Set up your organizer account in minutes. No credit card, no contract — just tickets that sell.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <Button asChild size="lg" className="rounded-full btn-glow bg-indigo-600 hover:bg-indigo-500 text-white hover:text-white text-base px-8">
                <Link to="/organizations/apply" data-testid="final-start-selling-button">
                  Start Selling Tickets Now <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Link to="/discover" className="text-sm text-slate-300 hover:text-white inline-flex items-center gap-1.5" data-testid="final-discover-link">
                <Compass className="h-4 w-4" /> Browse live events on Discover
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
